import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { Text } from '../../../../cdl/Text/Text';
import { queries } from '../../../../common/api/queryKeys/queries';
import { GenericError } from '../../../../common/Error/GenericError';
import { translate } from '../../../../common/helpers/translate.helper';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../../common/ui/Box';
import { useAuthenticationMethods } from '../../hooks/useAuthenticationMethods';
import { MfaAccessControl } from '../../hooks/useMfaAccessControl';
import { useMfaMethodTypes } from '../../hooks/useMfaMethodTypes';

import { AddAuthenticationMethodButton } from './AddAuthenticationMethodButton';
import { AuthenticationMethodsList } from './AuthenticationMethodsList';

interface MfaSectionProps {
    userId: string;
    accessControl: MfaAccessControl;
    showAddButton?: boolean;
}

export const MfaSection = ({ userId, accessControl, showAddButton = true }: MfaSectionProps) => {
    const queryClient = useQueryClient();
    const authMethodsQuery = useAuthenticationMethods(accessControl.isAnyAdminViewingOthers, userId);

    const methods = authMethodsQuery.data?.content || [];

    const { authenticatorAppData, hasTotpMethod, hasRecoveryCode } = useMfaMethodTypes(methods);

    // Auto-refresh MFA methods when window regains focus (user returns from enrollment)
    useEffect(() => {
        const handleWindowFocus = () => {
            // Invalidate and refetch MFA methods when user returns from external enrollment
            queryClient.invalidateQueries({ queryKey: queries.mfa.authenticationMethods.queryKey });
        };

        window.addEventListener('focus', handleWindowFocus);
        return () => window.removeEventListener('focus', handleWindowFocus);
    }, [queryClient, userId]);

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Box display="flex" flexDirection="column" gap={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={16}>
                    <Text variant="title" as="h2">
                        {translate('mfa.authenticator.title')}
                    </Text>
                    {authMethodsQuery.data && accessControl.canAddMfa && showAddButton ? (
                        <AddAuthenticationMethodButton hasTotpMethod={hasTotpMethod} />
                    ) : null}
                </Box>
                {!accessControl.isAnyAdminViewingOthers ? (
                    <Text variant="small" color="foreground.muted">
                        {translate('mfa.authenticator.description')}
                    </Text>
                ) : null}
            </Box>

            {authMethodsQuery.isPending ? <LoadingIndicator /> : null}
            {authMethodsQuery.isError ? <GenericError /> : null}

            {authMethodsQuery.data ? (
                <AuthenticationMethodsList
                    authenticatorAppData={authenticatorAppData}
                    hasRecoveryCode={hasRecoveryCode}
                    accessControl={accessControl}
                    userId={userId}
                />
            ) : null}
        </Box>
    );
};
